import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import {
  Button,
  Input,
  MobileFrame,
  OtpInput,
  PhoneInput,
  StickyActions,
  DevCodeButton,
} from '@waago/ui'
import { ApiError, useAuth, devCodeAvailable, fetchDevCode } from '@waago/api'

/**
 * AF sign-in (frames `4100:528` and `4472:4090`).
 *
 * Phone is still the identity (spec §1.2), but it is no longer a code that
 * proves it every time. Each SMS is billed, and the operator's own words were
 * "for one connection the system sends one OTP … it's very expensive". So the
 * password chosen at signup is the ordinary way in, and a code is left for the
 * two cases where nothing else works: a password nobody remembers, and an
 * account three wrong guesses have locked.
 */

/** The same floor the API enforces; checked here so the button says why. */
const MIN_PASSWORD = 8

/**
 * `code` is the unlock screen — a code and nothing else. `reset` asks for a
 * code *and* a new password, and serves both "I forgot mine" and "this account
 * never had one": same proof, same screen, and the person comes out of it with
 * a password rather than back where they started.
 */
type View = 'credentials' | 'code' | 'reset'

export default function LoginPage() {
  const navigate = useNavigate()
  const {
    state,
    login,
    loginWithPassword,
    requestUnlockCode,
    unlockWithCode,
    requestPasswordResetCode,
    resetPasswordWithCode,
  } = useAuth()
  const [view, setView] = useState<View>('credentials')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (state.status === 'authenticated') return <Navigate to="/" replace />

  const rateLimited = (err: unknown) => err instanceof ApiError && err.status === 429

  async function signIn() {
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      await loginWithPassword(phone, password)
    } catch (err) {
      if (err instanceof ApiError && err.is('password-not-set')) {
        // An account from before passwords existed — or a number with no
        // account at all, which answers identically on purpose. The reset
        // screen rather than a plain code login, so they come out of it with
        // a password instead of back where they started.
        await startReset(
          'Aucun mot de passe n’est défini pour ce numéro. Entrez le code reçu par SMS et' +
            ' choisissez-en un.',
        )
      } else if (err instanceof ApiError && err.is('account-locked')) {
        await sendUnlockCode()
      } else {
        setError(
          rateLimited(err)
            ? 'Trop de tentatives. Réessayez dans quelques minutes.'
            : 'Numéro ou mot de passe incorrect.',
        )
      }
    } finally {
      setBusy(false)
    }
  }

  async function sendUnlockCode() {
    setError(null)
    try {
      await requestUnlockCode(phone)
      setNotice(
        'Trop de tentatives : ce compte a été bloqué. Un code vient de vous être envoyé pour le' +
          ' débloquer.',
      )
      setCode('')
      setView('code')
    } catch (err) {
      setError(
        rateLimited(err)
          ? 'Trop de tentatives. Réessayez dans quelques minutes.'
          : "Impossible d'envoyer le code. Vérifiez le numéro.",
      )
    }
  }

  async function verify(value: string) {
    setError(null)
    setBusy(true)
    try {
      await unlockWithCode(phone, value)
    } catch {
      setError('Code incorrect ou expiré.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Sends the code for the reset screen.
   *
   * `why` is shown above it, because the person may not have asked for this —
   * they typed a password and were sent here.
   */
  async function startReset(why?: string) {
    setError(null)
    setNotice(why ?? null)
    if (phone.length < 12) {
      setError('Entrez d’abord votre numéro de téléphone.')
      return
    }
    setBusy(true)
    try {
      await requestPasswordResetCode(phone)
      setCode('')
      setNewPassword('')
      setView('reset')
    } catch (err) {
      setError(
        rateLimited(err)
          ? 'Trop de tentatives. Réessayez dans quelques minutes.'
          : "Impossible d'envoyer le code. Vérifiez le numéro.",
      )
    } finally {
      setBusy(false)
    }
  }

  async function confirmReset() {
    setError(null)
    setBusy(true)
    try {
      await resetPasswordWithCode(phone, code, newPassword)
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400 ? err.message : 'Code incorrect ou expiré.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function demoLogin() {
    setError(null)
    setBusy(true)
    try {
      await login('drive@transport.bj', 'waago-dev-password')
    } catch {
      setError('Connexion de démonstration indisponible.')
    } finally {
      setBusy(false)
    }
  }

  function backToCredentials() {
    setView('credentials')
    setCode('')
    setError(null)
    setNotice(null)
  }

  return (
    <MobileFrame>
      <main id="main" className="flex flex-1 flex-col">
        {/* The frame (`4100:528`) puts the wordmark on white, not on a brand
            banner: this is the screen someone reaches by opening the app, and
            the logo is the whole of the header. */}
        <div className="px-6 pt-[max(3rem,env(safe-area-inset-top))] pb-6">
          <p className="text-brand-600 text-3xl font-black tracking-tight">WAAGO</p>
          <p className="text-danger mt-1 text-[10px] font-semibold">Move. Deliver. Connect.</p>
        </div>

        <div className="flex flex-1 flex-col gap-5 p-6">
          {error ? (
            <p
              role="alert"
              className="animate-slide-up rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700"
            >
              {error}
            </p>
          ) : null}

          {notice && !error ? (
            <p className="animate-slide-up rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              {notice}
            </p>
          ) : null}

          {view === 'credentials' ? (
            <>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Connexion</h1>
                <p className="mt-2 text-base leading-6 text-slate-500">
                  Identifiez-vous avec votre numéro mobile et votre mot de passe.
                </p>
              </div>
              <PhoneInput
                label="Numéro de téléphone"
                value={phone}
                onValueChange={setPhone}
                placeholder="00 00 00 00 00"
                required
              />
              <Input
                label="Mot de passe"
                type="password"
                placeholder="Votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => void startReset()}
                disabled={busy}
                className="text-brand-600 self-end text-sm underline"
              >
                Mot de passe oublié&nbsp;?
              </button>
              <p className="text-center text-sm text-slate-500">
                Pas encore membre&nbsp;?{' '}
                <button
                  type="button"
                  onClick={() => void navigate('/inscription')}
                  className="text-brand-600 underline"
                >
                  Créer un compte
                </button>
              </p>
            </>
          ) : null}

          {view === 'code' ? (
            <>
              {/* Frame `4472:4090`. The number is repeated in full and the way
                  back is on the screen: mistyping a digit is the most likely
                  reason no code arrives, and without a way back the only way
                  out is to kill the app. */}
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Débloquer le compte</h1>
                <p className="mt-2 text-base leading-6 text-slate-500">
                  Veuillez entrer le code de confirmation envoyé au numéro{' '}
                  <span className="text-slate-900">{phone}</span>
                </p>
              </div>
              <OtpInput
                value={code}
                onChange={setCode}
                error={error ?? undefined}
                disabled={busy}
                onComplete={(v) => void verify(v)}
              />
              {/* Development only: the code is hashed at rest, so without
                  this nobody can get past this screen without the phone it
                  went to. */}
              <DevCodeButton
                available={devCodeAvailable()}
                destination={phone}
                fetchCode={fetchDevCode}
                onCode={(value) => {
                  setCode(value)
                  void verify(value)
                }}
              />
              <p className="text-center text-sm text-slate-500">
                Aucun code reçu&nbsp;?{' '}
                <button
                  type="button"
                  onClick={() => void sendUnlockCode()}
                  disabled={busy}
                  className="text-danger underline disabled:no-underline disabled:opacity-60"
                >
                  Renvoyer
                </button>
              </p>
            </>
          ) : null}

          {view === 'reset' ? (
            <>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Nouveau mot de passe</h1>
                <p className="mt-2 text-base leading-6 text-slate-500">
                  Entrez le code envoyé au <span className="text-slate-900">{phone}</span>, puis
                  choisissez un nouveau mot de passe.
                </p>
              </div>
              <OtpInput value={code} onChange={setCode} disabled={busy} />
              <DevCodeButton
                available={devCodeAvailable()}
                destination={phone}
                fetchCode={fetchDevCode}
                onCode={setCode}
              />
              <Input
                label="Nouveau mot de passe"
                type="password"
                placeholder={`Au moins ${MIN_PASSWORD} caractères`}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <p className="text-center text-sm text-slate-500">
                Aucun code reçu&nbsp;?{' '}
                <button
                  type="button"
                  onClick={() => void startReset()}
                  disabled={busy}
                  className="text-danger underline disabled:no-underline disabled:opacity-60"
                >
                  Renvoyer
                </button>
              </p>
            </>
          ) : null}
        </div>
      </main>

      <StickyActions>
        {view === 'credentials' ? (
          <Button
            block
            size="lg"
            disabled={phone.length < 12 || password.length < MIN_PASSWORD || busy}
            onClick={() => void signIn()}
          >
            {busy ? 'Connexion…' : 'Se connecter'}
          </Button>
        ) : null}

        {view === 'code' ? (
          <>
            <Button
              block
              size="lg"
              disabled={code.length < 6 || busy}
              onClick={() => void verify(code)}
            >
              {busy ? 'Vérification…' : 'Vérifier'}
            </Button>
            <p className="mt-3 text-center text-sm text-slate-500">
              <button type="button" className="text-danger underline" onClick={backToCredentials}>
                Revenir à la connexion
              </button>
            </p>
          </>
        ) : null}

        {view === 'reset' ? (
          <>
            <Button
              block
              size="lg"
              disabled={code.length < 6 || newPassword.length < MIN_PASSWORD || busy}
              onClick={() => void confirmReset()}
            >
              {busy ? 'Enregistrement…' : 'Enregistrer et se connecter'}
            </Button>
            <p className="mt-3 text-center text-sm text-slate-500">
              <button type="button" className="text-danger underline" onClick={backToCredentials}>
                Revenir à la connexion
              </button>
            </p>
          </>
        ) : null}

        {/* The same guard the code button above uses. Read directly,
            `import.meta.env.DEV` is false in the built bundle, so this
            button existed on the development machine and nowhere else. */}
        {devCodeAvailable() ? (
          <Button
            block
            variant="outline"
            className="mt-2"
            disabled={busy}
            data-testid="demo-login"
            onClick={() => void demoLogin()}
          >
            Connexion rapide (démo)
          </Button>
        ) : null}
      </StickyActions>
    </MobileFrame>
  )
}
